import React, { useRef, useEffect, useState } from "react";
import PropTypes from "prop-types";
import * as yup from "yup";
import { MCard } from "components/Shared";
import OrderStepButtons from "components/WebOrderV2/OrderStepButtons";
import { LENDER, COMMERCIAL } from "typeConstants/webOrder";
import {
  DATA_TYPE_DROPDOWN2,
  DATA_TYPE_ADDRESS,
  DATA_TYPE_BOOLEAN,
  DATA_TYPE_PHONE_NUMBER,
  DATA_TYPE_ADDRESS_AUTOCOMPLETE,
  DATA_TYPE_EMAIL,
  DATA_TYPE_NUMBER_STRING,
} from "typeConstants/dataTypes";
import { yesNoOptions } from "components/WebOrderV2/dropdownOptions";

const Homeowner = props => {
  const [hideMailingAddress, setHideMailingAddress] = useState(props.data?.hideMailingAddress);
  const [showEmailAddress, setShowEmailAddress] = useState(props.data?.validEmailAddress?.value);

  const mounted = useRef(true);
  const mCardControlMethodsHomeowner = useRef({});
  const mCardControlMethodsAddress = useRef({});

  useEffect(() => {
    return () => {
      mounted.current = false;
    };
  }, []);

  useEffect(() => {
    mCardControlMethodsHomeowner.current.refreshEditData();
  }, [showEmailAddress]);

  useEffect(() => {
    if (props.terminology !== LENDER) mCardControlMethodsAddress.current.refreshEditData();
  }, [hideMailingAddress]);

  const setControlMethodsHomeowner = () => {
    return cm => (mCardControlMethodsHomeowner.current = cm);
  };

  const setControlMethodsAddress = () => {
    return cm => (mCardControlMethodsAddress.current = cm);
  };

  const onPrevious = () => {
    props.onPrevious(getDataWithDropdownOptions());
  };

  const onNext = () => {
    let homeownerIsValid = mCardControlMethodsHomeowner.current.runValidation(true);
    let addressIsValid = props.terminology !== LENDER ? mCardControlMethodsAddress.current?.runValidation(true) : true;
    if (homeownerIsValid && addressIsValid) {
      props.onNext(getDataWithDropdownOptions());
    }
  };

  const getDataWithDropdownOptions = () => {
    let editedHomeowner = mCardControlMethodsHomeowner.current.getEditedDataObject();
    let editedAddress = hideMailingAddress || props.terminology === LENDER ? {} : mCardControlMethodsAddress.current?.getEditedDataObject();
    let editedData = { ...editedHomeowner, ...editedAddress };
    // Set dropdown values to object
    if (editedData.validEmailAddress !== undefined) {
      editedData.validEmailAddress = yesNoOptions.filter(y => y.value === editedData.validEmailAddress)[0];
    }
    if (editedData.mailingState) {
      editedData.mailingState = props.stateOptions.filter(s => s.value === editedData.mailingState)[0];
    }
    // Not sure why but MCard doesn't like to show this checkbox as having been edited
    editedData.hideMailingAddress = hideMailingAddress;
    return editedData;
  };

  const onMailingAddressSearch = address => {
    mCardControlMethodsAddress.current.setEditData("mailingAddress1", address.streetAddress);
    mCardControlMethodsAddress.current.setEditData("mailingCity", address.city);
    mCardControlMethodsAddress.current.setEditData("mailingState", { label: address.state, value: address.state });
    mCardControlMethodsAddress.current.setEditData("mailingZipCode", address.zip);
  };

  let homeownerTitle = props.terminology === LENDER ? "Borrower/Owner" : "Insured";

  const homeownerFields = [
    {
      key: "homeownerFirstName",
      label: "First Name",
      value: props.data.homeownerFirstName,
      validation: yup.string().max(50, "First name may not exceed 50 characters.").required("First Name is required"),
      placeholder: "Required",
    },
    {
      key: "homeownerLastName",
      label: "Last Name",
      value: props.data.homeownerLastName,
      validation: yup.string().max(50, "Last name may not exceed 50 characters.").required("Last Name is required"),
      placeholder: "Required",
    },
    {
      key: "homeownerPhoneNumber1",
      dataType: DATA_TYPE_PHONE_NUMBER,
      label: "Phone 1",
      value: props.data.homeownerPhoneNumber1
    },
    {
      key: "homeownerPhoneNumber2",
      dataType: DATA_TYPE_PHONE_NUMBER,
      label: "Phone 2",
      value: props.data.homeownerPhoneNumber2,
    },
    {
      key: "validEmailAddress",
      dataType: DATA_TYPE_DROPDOWN2,
      dataTypeOptions: { options: yesNoOptions },
      label: `Email Address Available for ${homeownerTitle}?`,
      value: props.data.validEmailAddress,
      onChange: option => setShowEmailAddress(option.value),
      validation: yup.string().required("Required"),
      placeholder: "Select... (required)",
    },
  ];

  const commercialFields = [
    {
      key: "businessName",
      label: "Business Name",
      value: props.data.businessName,
      validation: yup.string().max(50, "Business Name cannot exceed 50 characters").required("Business Name is required"),
      placeholder: "Required",
    },
    {
      key: "website",
      label: "Website Address",
      validation: yup.string().max(500, "Website cannot exceed 500 characters"),
      value: props.data.website,
    },
    {
      key: "businessPhone",
      dataType: DATA_TYPE_PHONE_NUMBER,
      label: "Business Phone",
      value: props.data.businessPhone
    },
  ];

  const emailField = [
    {
      key: "homeownerEmailAddress",
      dataType: DATA_TYPE_EMAIL,
      label: "Email",
      value: props.data.homeownerEmailAddress,
      validation: yup.string().max(100, "Email address may not exceed 100 characters.").required("Email is required").email("Email must be valid"),
      placeholder: "Required",
    },
  ];

  const hideMailingAddressField = [
    {
      key: "hideMailingAddress",
      dataType: DATA_TYPE_BOOLEAN,
      label: "Use Property Address for Mailing Address",
      value: props.data.hideMailingAddress,
      onChange: value => setHideMailingAddress(value),
    },
  ];

  const mailingAddressFields = [
    {
      key: "searchMailingAddress",
      dataType: DATA_TYPE_ADDRESS_AUTOCOMPLETE,
      label: "Address Search",
      onChange: onMailingAddressSearch,
      value: "",
    },
    {
      key: "mailingAddress1",
      dataType: DATA_TYPE_ADDRESS,
      label: "Mailing Address",
      value: props.data.mailingAddress1,
      validation: yup.string().max(75, "Address cannot exceed 75 characters").required("Address is required"),
      placeholder: "Required",
    },
    {
      key: "mailingCity",
      label: "Mailing City",
      value: props.data.mailingCity,
      validation: yup.string().max(50, "City cannot exceed 50 characters").required("City is required"),
      placeholder: "Required",
    },
    {
      key: "mailingState",
      dataType: DATA_TYPE_DROPDOWN2,
      label: "Mailing State",
      value: props.data.mailingState,
      dataTypeOptions: { options: props.stateOptions },
      validation: yup.string().required("State is required"),
      placeholder: "Select... (required)",

    },
    {
      key: "mailingZipCode",
      dataType: DATA_TYPE_NUMBER_STRING,
      label: "Mailing Zip",
      value: props.data.mailingZipCode,
      validation:  yup.string().min(5, "Value must be 5 digits").max(5, "Value must be 5 digits").required("Zip is required"),
      placeholder: "Required",
    },
  ];

  const getHomeownerFields = () => {
    if(props.pageLayout === COMMERCIAL) {
      return commercialFields;
    }
    else {
      let fields = homeownerFields;
      if (showEmailAddress) {
        fields.push(...emailField);
      }
      return fields;
    }
  };

  const getMailingAddressFields = () => {
    if(props.pageLayout === COMMERCIAL) { // hide "Use Property Address for Mailing Address"
      return mailingAddressFields;
    }
    else {
      let fields = hideMailingAddressField;
      if (!hideMailingAddress) {
        fields.push(...mailingAddressFields);
      }
      return fields;
    }
  };

  return (
    <>
      <MCard
        title={homeownerTitle}
        getControlMethods={setControlMethodsHomeowner("homeowner")}
        fields={getHomeownerFields()}
        onlyEditable
      />
      {props.terminology !== LENDER && <MCard
        title="Mailing Address"
        getControlMethods={setControlMethodsAddress("mailingAddress")}
        fields={getMailingAddressFields()}
        onlyEditable
      />}
      <OrderStepButtons
        currentStep={props.currentStep}
        onPrevious={onPrevious}
        onNext={onNext}
      />
    </>
  );
};

export default Homeowner;

Homeowner.propTypes = {
  currentStep: PropTypes.number,  // Current step in order process, needed for OrderStepButtons
  totalSteps: PropTypes.number,  // Total number of steps in order process, needed for OrderStepButtons
  data: PropTypes.object, // Data for MCard fields
  onNext: PropTypes.func, // Func to call when fields are valid to move to the next step
  onPrevious: PropTypes.func, // Func to call to move to previous step
  terminology: PropTypes.string, // Insurance or Lender, see typeConstants/webOrder.js
  pageLayout: PropTypes.string, // LenderAppraisal, LenderNonAppraisal, or Insurance, see typeConstants/webOrder.js
  accountFeatures: PropTypes.object, // Enabled customer features for selected account
  stateOptions: PropTypes.array,  // State abbrevation options for address field
};
